import { config } from "@config/config";
import React, { useCallback, useEffect, useState } from "react";

import { Button, SelectItem, Tooltip } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportReportStatusChoices,
  ApiReportTemplateExportFrameworkChoices,
  ExportTypeEnum,
} from "@api/graphql/types-and-hooks";
import { EXPORT_OPTIONS, ReportExportDialog } from "@components/ReportCompletionAndExport/ReportExportDialog";
import { useReportExport } from "@hooks/useReportExport";
import { useTemplateExport } from "@hooks/useTemplateExport";
import { ReportNode, useReport } from "@providers/ReportProvider";

interface ClinicalReportExportProps {
  reportData: ReportNode;
  refetchReportData: () => Promise<void>;
  isReportLoading?: boolean;
}

const ClinicalReportExport: React.FC<ClinicalReportExportProps> = ({
  reportData,
  refetchReportData,
  isReportLoading,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const [selectedExportType, setSelectedExportType] = useState<SelectItem>(EXPORT_OPTIONS[0]);

  const { reportQuery } = useReport();
  const { refetch: refetchReport } = reportQuery;

  // Use provided isReportLoading prop if available, otherwise fall back to reportQuery.isFetching
  const reportLoadingState = isReportLoading !== undefined ? isReportLoading : reportQuery.isFetching;

  const clientName =
    reportData.reportClientDetails?.clientFirstName && reportData.reportClientDetails?.clientLastName
      ? `${reportData.reportClientDetails.clientFirstName} ${reportData.reportClientDetails.clientLastName}`
      : "Client";

  const exportFramework = reportData?.reportTemplate?.exportFramework;
  const isUnifiedExport = reportData?.reportTemplate?.unifiedExport ?? false;

  const isDocxTemplater = exportFramework === ApiReportTemplateExportFrameworkChoices.Docxtemplater && !isUnifiedExport;
  const isHandlebars =
    exportFramework === ApiReportTemplateExportFrameworkChoices.Handlebars ||
    (isUnifiedExport && exportFramework === ApiReportTemplateExportFrameworkChoices.Docxtemplater);

  const { handleExport, isExporting, exportError } = useReportExport({
    report: reportData,
    clientName,
    exportType: selectedExportType.id as ExportTypeEnum,
    onExportComplete: () => setIsExportModalOpen(false),
  });

  const { handleExport: handleDocxTemplaterExport, isExporting: isDocxTemplaterExporting } = useTemplateExport({
    reportData,
  });

  const isFileReady = (report: ReportNode) => {
    return (
      report.reportStatus === ApiReportReportStatusChoices.Completed &&
      (report.reportDocxFileSignedUrl || report.reportPdfFileSignedUrl)
    );
  };

  const pollReportStatus = useCallback(async () => {
    const maxAttempts = 60; // Poll for up to 5 minutes
    let attempts = 0;

    setIsGenerating(true);
    setGenerationError(null);

    const poll = async (): Promise<void> => {
      try {
        const result = await refetchReport();
        const report = result.data?.getReports?.edges[0]?.node;

        if (!report) {
          throw new Error("Report not found");
        }

        if (isFileReady(report)) {
          setIsGenerating(false);
          return;
        }

        if (report.reportStatus === ApiReportReportStatusChoices.Failure) {
          throw new Error("Report generation failed");
        }

        if (report.reportStatus === ApiReportReportStatusChoices.Generating) {
          attempts++;
          if (attempts >= maxAttempts) {
            throw new Error("Report generation timeout - please try again");
          }
          setTimeout(poll, config.REPORT_POLLING_INTERVAL || 5000);
          return;
        }

        attempts++;
        if (attempts >= maxAttempts) {
          throw new Error("Report generation timeout - please try again");
        }
        setTimeout(poll, config.REPORT_POLLING_INTERVAL || 5000);
      } catch (error) {
        console.error("Report polling error:", error);
        setIsGenerating(false);
        setGenerationError(error instanceof Error ? error.message : "An error occurred during report generation");
      }
    };

    await poll();
  }, [refetchReport]);

  useEffect(() => {
    if (!isFileReady(reportData) && reportData.reportStatus === ApiReportReportStatusChoices.Generating) {
      pollReportStatus();
    }
  }, [reportData, pollReportStatus]);

  const isFileCurrentlyReady = isDocxTemplater
    ? reportData.reportStatus === ApiReportReportStatusChoices.Completed
    : isFileReady(reportData);
  // A report which previously *only* rendered via the local templater will not have a valid S3 url to download from.
  // This allows any older reports to be regenerated with the backend templating instead of falling back to local templating.
  const shouldRetryMissingFile =
    isHandlebars &&
    !isFileCurrentlyReady &&
    !isGenerating &&
    reportData.reportStatus !== ApiReportReportStatusChoices.Generating;
  const isDisabled =
    isExporting || isDocxTemplaterExporting || isGenerating || (!isFileCurrentlyReady && !shouldRetryMissingFile);
  const error = exportError || generationError;

  const getButtonText = () => {
    if (isExporting || isDocxTemplaterExporting) return "Downloading...";
    if (error || shouldRetryMissingFile) return "Retry Export";
    return "Export";
  };

  const getTooltipText = () => {
    if (
      isGenerating ||
      (!isFileCurrentlyReady && reportData.reportStatus === ApiReportReportStatusChoices.Generating)
    ) {
      return "Generating...";
    }
    if (!isFileCurrentlyReady && !shouldRetryMissingFile) {
      return "File not ready";
    }
    return undefined;
  };

  return (
    <div className="flex flex-col items-end">
      {getTooltipText() ? (
        <Tooltip className="whitespace-pre-line" text={getTooltipText()} showInfoIcon={false}>
          <Button
            text={getButtonText()}
            disabled={isDisabled}
            id="clinical-report-export-btn"
            appearance="primary"
            onClick={() => {
              if (isDocxTemplater) {
                handleDocxTemplaterExport();
              } else if (isHandlebars) {
                setIsExportModalOpen(true);
              } else {
                // GoogleDocs or other frameworks - export directly
                handleExport();
              }
            }}
          />
        </Tooltip>
      ) : (
        <Button
          text={getButtonText()}
          disabled={isDisabled}
          id="clinical-report-export-btn"
          appearance="primary"
          onClick={() => {
            if (isDocxTemplater) {
              handleDocxTemplaterExport();
            } else if (isHandlebars) {
              setIsExportModalOpen(true);
            } else {
              // GoogleDocs or other frameworks - export directly
              handleExport();
            }
          }}
        />
      )}
      {error && <p className="text-red-600 text-sm mt-1 max-w-xs text-right">{error}</p>}

      <ReportExportDialog
        open={isExportModalOpen}
        setOpen={() => setIsExportModalOpen(false)}
        onExport={async () => {
          await handleExport();
        }}
        selectedExportType={selectedExportType}
        setSelectedExportType={setSelectedExportType}
        isExporting={isExporting}
        report={reportData}
        onRetrySuccess={async () => {
          await refetchReportData();
        }}
        isReportLoading={reportLoadingState}
      />
    </div>
  );
};

export default ClinicalReportExport;
